import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { BookHeart, ShieldCheck } from "lucide-react";

/**
 * Owner-only test login. Reads `?k=<secret>` from the URL, hands it to the
 * backend as a header (so the token itself never lives in `location.href`
 * beyond the first paint — we strip it via `replaceState` immediately), then
 * stores the JWT the backend returns and redirects to /journal.
 *
 * If the env-var gate on the server is empty, the backend responds 404 and
 * this page shows a friendly "test access disabled" state.
 */
export default function TestLoginPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const ran = useRef(false);
  const [status, setStatus] = useState("Signing you in…");
  const [ok, setOk] = useState(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const key = search.get("k");
    // Read the desired destination BEFORE we strip params from the URL.
    // Default to /journal to preserve existing behaviour.
    const toRaw = search.get("to");
    const to = toRaw && toRaw.startsWith("/") ? toRaw : "/journal";
    // Strip the key from the URL immediately so it isn't preserved in
    // browser history or shared if the user copies the URL after landing.
    if (key) {
      try {
        window.history.replaceState({}, "", "/test-login");
      } catch {}
    }

    (async () => {
      if (!key) {
        setStatus("Missing access key.");
        setOk(false);
        return;
      }
      try {
        // Clear any stale token from a previous non-admin session so the
        // owner test-login always wins the race and no ghost identity
        // sneaks into the SPA before we finish signing in.
        try { localStorage.removeItem("lg_token"); } catch {}
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const keep = search.get("keep") === "1";
        const headers = { "X-Test-Login-Key": key };
        if (keep) headers["X-Test-Keep-State"] = "1";
        const res = await axios.post(
          `${base}/api/auth/owner-test-login`,
          {},
          { headers },
        );
        localStorage.setItem("lg_token", res.data.token);
        await refresh();
        const isAdminDest = to.startsWith("/admin");
        setStatus(
          isAdminDest
            ? "Signed in. Opening the Master Admin Dashboard…"
            : "Signed in. Opening your Journal…",
        );
        setOk(true);
        setTimeout(() => navigate(to, { replace: true }), 600);
      } catch (err) {
        const code = err?.response?.status;
        setStatus(
          code === 404
            ? "Test access is currently disabled."
            : "Sign-in failed. Please try again.",
        );
        setOk(false);
      }
    })();
  }, [search, navigate, refresh]);

  return (
    <div className="min-h-screen bg-[#F9FAF7] flex items-center justify-center p-6">
      <div className="max-w-sm w-full rounded-3xl border border-[#DCE5DB] bg-white p-8 shadow-[0_4px_12px_rgba(0,0,0,0.04)] text-center fade-in-up">
        <span className="mx-auto w-12 h-12 rounded-2xl bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center mb-4">
          {ok === false ? <ShieldCheck size={20} /> : <BookHeart size={20} />}
        </span>
        <h1 className="font-display text-xl text-[#2E2E2E] mb-2">
          Owner test access
        </h1>
        <p className="text-sm text-[#6B6B6B] leading-relaxed">{status}</p>
      </div>
    </div>
  );
}
